import {
  ActiveSimCardDetails,
  ActiveSimCardsDeviceInfoFragment,
  AvailableAndActiveSimCardsQuery,
  DeactivateSimCardModal,
  InfoBlock,
  SimCardFragment,
  useCurrentSubscriptionId,
  StepUpAuthentication,
  useAuthenticationStepUpCache,
  AuthenticationStepUpCache,
  SimCardWithOrderButton,
  SimPaidByStatusMessage,
} from "@phonero/common-ux"
import { Maybe } from "graphql/jsutils/Maybe"
import {
  DateFormat,
  PuxItem,
  PuxModal,
  PuxSkeletonText,
  useTranslation,
} from "@phonero/pux-react"
import {
  ApiProductState,
  OfferingContractCategory,
  SimCardType2,
} from "@phonero/common-graphql/types"
import React, { useState } from "react"
import { useFeatureIsOn, useFeatureValue } from "@growthbook/growthbook-react"
import ShinySimCard from "../ShinySimCard"
import css from "./SimCardSlides.module.scss"
import { SimCardWizard } from "../SimCardWizard/SimCardWizard"
import { GenericSimCardFragment } from "./SimCardSlides"
import { QrCodeDetails } from "./QrCodeDetails"
import { SimReadyForActivationText } from "./SimReadyForActivationText"
import { ReadyForActivationDetails } from "./ReadyForActivationDetails"
import { useHistory } from "react-router-dom"
import { phoneroConfig } from "../../../../appConstants"

export const SimCardListItem = ({
  products,
  phoneNumber,
  data,
  simCardDetailsloading,
  simCardsDeviceInfo,
  editDenied,
  firstAvailableCard,
  availableToOrder,
  ...rest
}: {
  data: AvailableAndActiveSimCardsQuery | undefined
  simCardDetailsloading: boolean
  phoneNumber: Maybe<string | number>
  products: Array<SimCardFragment & { phoneNumber: Maybe<string | number> }>
  subscriptionDeactivationDate: Date | undefined
  subscriptionExpectedActivationDate: Date | undefined
  updateSimCard: (contractProductId: number, newName: string) => Promise<void>
  simCardsDeviceInfo: Array<ActiveSimCardsDeviceInfoFragment> | undefined,
  editDenied?: boolean
  firstAvailableCard?: SimCardFragment
  availableToOrder?: number
}) => {
  const { i18n } = useTranslation()
  const history = useHistory()
  const {
    isCacheActive,
    cachedItem,
    clear: clearAuthenticationCache,
  } = useAuthenticationStepUpCache()

  const { subscriptionId } = useCurrentSubscriptionId()
  const enableSimWithoutPin =
    data?.mobileSubscription?.contractCategory === OfferingContractCategory.M2M

  const [orderSim, setOrderSim] = useState<GenericSimCardFragment>()
  const [simCardToDeactivate, setSimCardToDeactivate] =
    useState<SimCardFragment>()
  const onDeactivateDismiss = () => {
    setSimCardToDeactivate(undefined)
    history.goBack()
  }
  const showEditSimWizard = useFeatureValue("dpw-edit-sim-wizard", true)
  const showReplaceSimOnSmartwatch = useFeatureIsOn(
    "selfservice-allow-deactivate-smartwatchsim"
  )
  const showVippsRecurringOrderSimCard = useFeatureIsOn(
    "DP-order-sim-vipps-recurring"
  )

  const { __typename, ...savedAddress } =
    data?.mobileSubscription?.simCardAddress! ?? ""

  // Open replace sim wizard if cache is active
  useEffect(() => {
    if (
      !!isCacheActive &&
      cachedItem?.context === "ReplaceSim" &&
      !!cachedItem.replaceSim
    ) {
      setOrderSim(cachedItem.replaceSim.OrderSim)
      clearAuthenticationCache()
    }
  }, [isCacheActive, cachedItem, setOrderSim, clearAuthenticationCache])

  const replaceSimCache = (): AuthenticationStepUpCache => {
    const replaceSimAuthCache: AuthenticationStepUpCache = {
      context: "ReplaceSim",
      replaceSim: { OrderSim: orderSim },
    }
    return replaceSimAuthCache
  }
  const showPrice = phoneroConfig.collectorApplicationName !== "dpw"

  return (
    <>
      {products
        .filter(({ state }) => state !== ApiProductState.Available)
        .map(({ phoneNumber: p, ...card }, index) => {
          const device = simCardsDeviceInfo?.find((s) => s.id === card.id)
          return (
            <PuxItem key={`${card?.id + "_" + card.state}-${index}`}>
              <div slot="start">
                <ShinySimCard
                  phoneNumber={p || phoneNumber}
                  {...card}
                  {...rest}
                />
              </div>
              {simCardDetailsloading ? (
                <PuxSkeletonText
                  animated={simCardDetailsloading}
                  style={{ width: 200 }}
                />
              ) : (
                card.state === ApiProductState.Active &&
                !card.embeddedSimIsReadyForActivation &&
                device && (
                  <>
                    <div>
                      {showVippsRecurringOrderSimCard &&
                        card.type !== SimCardType2.Subscription && (
                          <SimPaidByStatusMessage paidBy="Betales privat med " />
                        )}
                      <ActiveSimCardDetails
                        mode={"light"}
                        deviceName={device.deviceInfo?.deviceName}
                        lastUsed={device.deviceInfo?.lastUsed}
                        usage={device.usage}
                        activationDate={card.activationDate}
                        price={card.price}
                        showPrice={showPrice}
                      />
                    </div>
                  </>
                )
              )}
              {card.state === ApiProductState.Startactivation &&
                !card.activationDate && (
                  <>
                    <SimReadyForActivationText card={card} />
                    <ReadyForActivationDetails card={card} />
                  </>
                )}
              {card.embeddedSimIsReadyForActivation && (
                <SimReadyForActivationText card={card} />
              )}
              <div className={css["sim-buttons"]}>
                {card.embeddedSimIsReadyForActivation && (
                  <QrCodeDetails card={card} />
                )}
                {showEditSimWizard &&
                  !editDenied &&
                  card.type !== SimCardType2.Smartwatch &&
                  card.state === ApiProductState.Active && (
                    <PuxItem
                      type="button"
                      button={true}
                      onClick={() =>
                        setOrderSim(card as GenericSimCardFragment)
                      }
                    >
                      Erstatt
                    </PuxItem>
                  )}
                {card.type === SimCardType2.Smartwatch &&
                  card.state === ApiProductState.Active &&
                  !card.deactivationDate &&
                  showReplaceSimOnSmartwatch && (
                    <PuxItem
                      type="button"
                      button={true}
                      onClick={() => setSimCardToDeactivate(card)}
                    >
                      Avslutt
                    </PuxItem>
                  )}
              </div>
              <div style={{ padding: "10px 20px 10px 10px" }}>
                {card.type !== SimCardType2.Subscription &&
                  !!card.deactivationDate && (
                    <InfoBlock className={css.infoBlock}>
                      Avsluttes{" "}
                      {i18n.format(
                        card!.deactivationDate,
                        DateFormat.DateTimeLong
                      )}
                    </InfoBlock>
                  )}
              </div>
            </PuxItem>
          )
        })}
      {showVippsRecurringOrderSimCard && firstAvailableCard && (
        <>
          <PuxItem>
            <SimCardWithOrderButton
              card={firstAvailableCard}
              setOrderSim={() => {
                setOrderSim(firstAvailableCard)
              }}
              availableToOrder={availableToOrder}
              dittPhonero
              payByVipps
            />
          </PuxItem>
        </>
      )}
      {!!simCardToDeactivate && (
        <DeactivateSimCardModal
          card={simCardToDeactivate}
          isOpen={simCardToDeactivate !== undefined}
          onDidDismiss={onDeactivateDismiss}
          disableDateSelection={true}
          showPrice={showPrice}
        />
      )}
      <PuxModal
        isOpen={!!orderSim}
        onDidDismiss={() => {
          setOrderSim(undefined)
          clearAuthenticationCache()
        }}
      >
        <StepUpAuthentication
          title="Erstatt SIM-kort"
          stepUpCache={replaceSimCache}
        >
          <SimCardWizard
            subscriptionId={subscriptionId ?? ""}
            enableSimWithoutPin={enableSimWithoutPin}
            card={orderSim as any}
            cardType={orderSim?.type!}
            address={savedAddress}
            onClose={() => setOrderSim(undefined)}
            userName={data?.mobileSubscription?.userName ?? undefined}
            msisdn={data?.mobileSubscription?.msisdn ?? undefined}
          />
        </StepUpAuthentication>
      </PuxModal>
    </>
  )
}
